import { useCallback, useEffect, useMemo, useState } from "react"
import { useHistory, useLocation } from "react-router-dom"
import { useRegulatorySession } from "../../providers/regulatoryProvider"
import type { CatalogFilters, RegulatoryScope } from "../../types/regulatory"
export function useCatalogResource<T>(load: (signal: AbortSignal) => Promise<T>) {
  const { sessionKey } = useRegulatorySession()
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<Error | null>(null)
  const [loading, setLoading] = useState(true)
  const [generation, setGeneration] = useState(0)
  const reload = useCallback(() => setGeneration((value) => value + 1), [])
  useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    setError(null)
    setData(null)
    load(controller.signal)
      .then((value) => {
        if (!controller.signal.aborted) setData(value)
      })
      .catch((reason) => {
        if (!controller.signal.aborted && reason?.name !== "AbortError")
          setError(reason instanceof Error ? reason : new Error(String(reason)))
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [load, generation, sessionKey])
  return { data, error, loading, reload }
}
export function useCatalogFilters() {
  const history = useHistory()
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const parsedPage = Math.trunc(Number(params.get("page")))
  const page = Number.isSafeInteger(parsedPage) ? Math.max(1, parsedPage) : 1
  const limit = Math.min(200, Math.max(1, Math.trunc(Number(params.get("limit"))) || 25))
  const scope = params.get("scope") as RegulatoryScope | null
  const filters: CatalogFilters = { offset: (page - 1) * limit, limit }
  if (scope && ["site", "common", "equipment"].includes(scope)) filters.scope = scope
  for (const key of ["namespace", "templateUuid", "packUuid", "q"] as const) {
    const value = params.get(key)
    if (value) filters[key] = value
  }
  for (const key of ["archived", "validated"] as const) {
    const value = params.get(key)
    if (value === "true" || value === "false") filters[key] = value === "true"
  }
  const update = useCallback(
    (patch: Record<string, string | number | boolean | undefined>) => {
      const next = new URLSearchParams(location.search)
      if (!("page" in patch)) next.delete("page")
      for (const [key, value] of Object.entries(patch)) {
        if (value === undefined || value === "") next.delete(key)
        else next.set(key, String(value))
      }
      history.replace({ pathname: location.pathname, search: next.toString() })
    },
    [history, location.pathname, location.search],
  )
  const filterKey = JSON.stringify(filters)
  const stableFilters = useMemo(() => JSON.parse(filterKey) as CatalogFilters, [filterKey])
  return { filters: stableFilters, page, update, filterKey }
}
